import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { api, extractErrorMessage, getAccessToken } from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * Contexte global Notifications (Phase 5 — US-NOTIF-01/02/03/05/06).
 *
 *  - Maintient une connexion WebSocket vers `/ws/notifications?token=<JWT>`
 *    avec auto-reconnect backoff exponentiel (1s → 2s → 4s … max 30s).
 *  - Charge l'historique récent en REST au montage + à la reconnexion.
 *  - Réception WS d'un `notification.new` :
 *      • incrémente le compteur
 *      • prepend dans la liste in-memory
 *      • joue un son si `sound_enabled` (préférences)
 *      • affiche une notification navigateur si `browser_push_enabled` +
 *        permission accordée
 *  - Expose des actions optimistes (markRead, markAllRead, dismiss).
 *  - Se déconnecte proprement au logout / unmount.
 */

export type NotificationPriority = 'normal' | 'high' | 'urgent';

export interface NotificationItem {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  module: string;
  related_entity_id: string | null;
  action_url: string | null;
  priority: NotificationPriority;
  is_read: boolean;
  metadata: unknown;
  created_at: string;
  read_at: string | null;
}

export interface NotificationPreferences {
  sound_enabled: boolean;
  browser_push_enabled: boolean;
  email_for_urgent: boolean;
  email_for_high: boolean;
  email_for_normal: boolean;
  digest_frequency: 'never' | 'daily' | 'weekly';
}

export interface NotificationsApi {
  /** Liste in-memory des notifications les plus récentes (max 50). */
  recent: NotificationItem[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  connectionState: 'connecting' | 'open' | 'closed';

  refresh(): Promise<void>;
  markRead(id: string): Promise<void>;
  markUnread(id: string): Promise<void>;
  markAllRead(): Promise<void>;
  dismiss(id: string): Promise<void>;
  updatePreferences(input: Partial<NotificationPreferences>): Promise<void>;

  /** Demande la permission navigateur (idempotent). */
  requestBrowserPermission(): Promise<NotificationPermission>;
  /** État de la permission Web Notifications. */
  browserPermission: NotificationPermission | null;
}

const NotificationsCtx = createContext<NotificationsApi | null>(null);

const RECENT_LIMIT = 50;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];

interface BroadcastMessage {
  kind: string;
  data: unknown;
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [recent, setRecent] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [connectionState, setConnectionState] = useState<'connecting' | 'open' | 'closed'>(
    'closed',
  );
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(
    typeof Notification !== 'undefined' ? Notification.permission : null,
  );

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const closedByLogoutRef = useRef(false);
  const preferencesRef = useRef<NotificationPreferences | null>(null);
  preferencesRef.current = preferences;

  // ─── Charge l'historique récent (REST) ─────────────────────────────────────

  const loadRecent = useCallback(async () => {
    try {
      const [listRes, prefsRes] = await Promise.all([
        api.get<{
          data: NotificationItem[];
          pagination: { total: number };
        }>(`/notifications?page_size=${RECENT_LIMIT}`),
        api.get<{ data: NotificationPreferences }>('/notifications/preferences'),
      ]);
      setRecent(listRes.data.data);
      setUnreadCount(listRes.data.data.filter((n) => !n.is_read).length);
      setPreferences(prefsRes.data.data);
    } catch (err) {
      // Silencieux côté UI — le user voit la cloche désactivée si la
      // connexion n'aboutit pas, mais c'est non-bloquant.
      console.warn('[notifications] Failed to load:', extractErrorMessage(err));
    }
  }, []);

  // ─── Sound + Web Notifications ─────────────────────────────────────────────

  const playSound = useCallback(() => {
    if (!preferencesRef.current?.sound_enabled) return;
    try {
      // Bip court synthétisé via Web Audio API — pas de fichier à hoster.
      const ctx = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      )();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {
      // Pas d'API audio : silencieusement ignoré.
    }
  }, []);

  const showBrowserNotification = useCallback((notif: NotificationItem) => {
    if (!preferencesRef.current?.browser_push_enabled) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(notif.title, {
        body: notif.message,
        tag: notif.id,
        icon: '/favicon.ico',
      });
      n.onclick = () => {
        window.focus();
        if (notif.action_url) {
          window.location.assign(notif.action_url);
        }
        n.close();
      };
    } catch {
      // Silencieux — quotas, permissions révoquées, etc.
    }
  }, []);

  const requestBrowserPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission !== 'default') {
      setBrowserPermission(Notification.permission);
      return Notification.permission;
    }
    const result = await Notification.requestPermission();
    setBrowserPermission(result);
    return result;
  }, []);

  // ─── WebSocket ─────────────────────────────────────────────────────────────

  const handleBroadcast = useCallback(
    (msg: BroadcastMessage) => {
      if (msg.kind === 'notification.new') {
        const notif = msg.data as NotificationItem;
        setRecent((prev) => {
          const next = [notif, ...prev.filter((n) => n.id !== notif.id)];
          return next.slice(0, RECENT_LIMIT);
        });
        setUnreadCount((c) => c + (notif.is_read ? 0 : 1));
        playSound();
        showBrowserNotification(notif);
      } else if (msg.kind === 'notification.update') {
        const data = msg.data as
          | { id: string; is_read?: boolean; read_at?: string | null }
          | { all_read: true };
        if ('all_read' in data) {
          setRecent((prev) =>
            prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
          );
          setUnreadCount(0);
        } else {
          setRecent((prev) =>
            prev.map((n) =>
              n.id === data.id
                ? { ...n, is_read: data.is_read ?? n.is_read, read_at: data.read_at ?? null }
                : n,
            ),
          );
          setUnreadCount((c) => {
            // Si la notif passait de unread→read, c-1. Sinon recompute.
            return Math.max(0, c + (data.is_read ? -1 : 1));
          });
        }
      } else if (msg.kind === 'notification.deleted') {
        const data = msg.data as { id?: string; all_read?: true };
        if (data.all_read) {
          setRecent((prev) => prev.filter((n) => !n.is_read));
        } else if (data.id) {
          setRecent((prev) => {
            const removed = prev.find((n) => n.id === data.id);
            if (removed && !removed.is_read) {
              setUnreadCount((c) => Math.max(0, c - 1));
            }
            return prev.filter((n) => n.id !== data.id);
          });
        }
      }
    },
    [playSound, showBrowserNotification],
  );

  const connect = useCallback(() => {
    if (!user || user.is_platform_admin) return;
    const token = getAccessToken();
    if (!token) return;

    closedByLogoutRef.current = false;
    setConnectionState('connecting');

    // Vite proxy `/ws` → backend (cf. vite.config.ts), avec `ws: true` activé.
    // En prod, Caddy fait le même mapping. URL construite dynamiquement
    // selon le protocole de la page (wss:// si HTTPS).
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}/ws/notifications?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(url);
    socketRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setConnectionState('open');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as BroadcastMessage;
        handleBroadcast(msg);
      } catch {
        // Ignored — message non-JSON.
      }
    };

    ws.onerror = () => {
      // onclose se chargera de la reconnexion.
    };

    ws.onclose = (event) => {
      setConnectionState('closed');
      socketRef.current = null;
      if (closedByLogoutRef.current) return;
      // 4401/4403 = pas de retry (auth ratée).
      if (event.code === 4401 || event.code === 4403) return;
      const attempt = reconnectAttemptsRef.current;
      const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)] ?? 30000;
      reconnectAttemptsRef.current = attempt + 1;
      reconnectTimerRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };
  }, [user, handleBroadcast]);

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user || user.is_platform_admin) {
      setRecent([]);
      setUnreadCount(0);
      setPreferences(null);
      return;
    }
    loadRecent();
    connect();
    return () => {
      closedByLogoutRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      socketRef.current?.close(1000, 'unmount');
      socketRef.current = null;
    };
    // user.id : on relance la connexion à chaque changement d'utilisateur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.is_platform_admin]);

  // ─── Actions (REST) ────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    await loadRecent();
  }, [loadRecent]);

  const markRead = useCallback(
    async (id: string) => {
      setRecent((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await api.post(`/notifications/${id}/read`);
      } catch (err) {
        console.warn('[notifications] markRead failed:', extractErrorMessage(err));
        // Rollback : on re-fetch pour récupérer l'état serveur authoritatif.
        loadRecent();
      }
    },
    [loadRecent],
  );

  const markUnread = useCallback(
    async (id: string) => {
      setRecent((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: false, read_at: null } : n)),
      );
      setUnreadCount((c) => c + 1);
      try {
        await api.post(`/notifications/${id}/unread`);
      } catch (err) {
        console.warn('[notifications] markUnread failed:', extractErrorMessage(err));
        loadRecent();
      }
    },
    [loadRecent],
  );

  const markAllRead = useCallback(async () => {
    setRecent((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
    try {
      await api.post('/notifications/mark-all-read');
    } catch (err) {
      console.warn('[notifications] markAllRead failed:', extractErrorMessage(err));
      loadRecent();
    }
  }, [loadRecent]);

  const dismiss = useCallback(
    async (id: string) => {
      let removedUnread = false;
      setRecent((prev) => {
        const removed = prev.find((n) => n.id === id);
        if (removed && !removed.is_read) removedUnread = true;
        return prev.filter((n) => n.id !== id);
      });
      if (removedUnread) setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await api.delete(`/notifications/${id}`);
      } catch (err) {
        console.warn('[notifications] dismiss failed:', extractErrorMessage(err));
        loadRecent();
      }
    },
    [loadRecent],
  );

  const updatePreferences = useCallback(
    async (input: Partial<NotificationPreferences>) => {
      setPreferences((prev) => (prev ? { ...prev, ...input } : prev));
      try {
        const res = await api.patch<{ data: NotificationPreferences }>(
          '/notifications/preferences',
          input,
        );
        setPreferences(res.data.data);
      } catch (err) {
        console.warn('[notifications] updatePreferences failed:', extractErrorMessage(err));
        loadRecent();
      }
    },
    [loadRecent],
  );

  const value = useMemo<NotificationsApi>(
    () => ({
      recent,
      unreadCount,
      preferences,
      connectionState,
      browserPermission,
      refresh,
      markRead,
      markUnread,
      markAllRead,
      dismiss,
      updatePreferences,
      requestBrowserPermission,
    }),
    [
      recent,
      unreadCount,
      preferences,
      connectionState,
      browserPermission,
      refresh,
      markRead,
      markUnread,
      markAllRead,
      dismiss,
      updatePreferences,
      requestBrowserPermission,
    ],
  );

  return <NotificationsCtx.Provider value={value}>{children}</NotificationsCtx.Provider>;
}

export function useNotifications(): NotificationsApi {
  const ctx = useContext(NotificationsCtx);
  if (!ctx) {
    throw new Error('useNotifications must be used inside <NotificationsProvider>');
  }
  return ctx;
}
